import { useEffect } from 'react'
import { type Moment } from 'moment'
import { type SimpleOption } from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import {
  type GetInsightsPortalGroupComparisonQueryVariables,
  type GetInsightsPortalMultiGroupDataQueryVariables,
  InsightsPortalGroupComparisonRequestJoinBy,
  InsightsPortalOwner,
  type InsightsPortalSchema,
  type InsightsPortalValue,
  type InsightsPortalValueInput,
  useGetInsightsPortalFilterOptionsLazyQuery,
  useGetInsightsPortalGroupComparisonQuery,
  useGetInsightsPortalMetricsQuery,
  useGetInsightsPortalMultiGroupDataQuery,
} from 'domains/insights-portal/api'
import {
  type BaseDynamicFilterOptions,
  insightsPortalDateFormat,
  transformApiOptionsIntoDashboardOptions,
  type transformFilterParamsToApiFilterSelectionsFactory,
} from 'domains/insights-portal/schemas/utils/insightsPortalSchemas.utils'
import { useInsightsPortalAuthorizationContext } from 'domains/insights-portal/utils/authorization-context.hooks'
import { type FilterParamsState } from 'domains/insights-portal/utils/filter-params/filterParams.types'
import { useFilterParamsContext } from 'domains/insights-portal/utils/filter-params/filterParamsContext'
import { calculateComparisonPeriod } from 'domains/insights-portal/utils/insights-portal.utils'
import { useInsightsPortalQueryTags } from 'domains/insights-portal/utils/query-tags.hooks'
import { useSchema } from 'domains/insights-portal/utils/schema.hooks'

/*
  this is a factory that creates hooks that allow you to interact with the useGetInsightsPortalFilterOptionsLazyQuery query for a specific schema.
  user needs to define which schema to use, a transformation function that transforms the filter selections for the api, a config object for mapping out the value types to read from each filter, and
  an object for converting the api filter names to dashboard filter names
*/

export function createFilterOptionsHook(
  schema: InsightsPortalSchema,
  transformFiltersToApi: ReturnType<typeof transformFilterParamsToApiFilterSelectionsFactory>,
  transformApiOptionsConfig: { [filterName: string]: keyof InsightsPortalValue },
  mapApiNameToDashboardName: { [apiFilterName: string]: string },
  whitelistFilterOptions?: string[]
) {
  const useFilters = (customFiltersValues?: { [key: string]: SimpleOption[] | string }) => {
    const [getFilters, { data, loading, error, called }] =
      useGetInsightsPortalFilterOptionsLazyQuery()

    const { selectedFiltersValues, dates } = useFilterParamsContext()

    const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

    const schemaName = useSchema(schema)

    const queryTags = useInsightsPortalQueryTags()

    useEffect(() => {
      const filterValues = { ...selectedFiltersValues, ...customFiltersValues }
      getFilters({
        variables: {
          schema: schemaName,
          partnerIds,
          retailerIds,
          retailerLocationIds,
          filters: transformFiltersToApi(filterValues, dates),
          whitelistFilterOptions,
          owner: InsightsPortalOwner.RetailerTools,
          queryTags,
        },
      })
    }, [
      customFiltersValues,
      selectedFiltersValues,
      getFilters,
      dates,
      partnerIds,
      retailerIds,
      retailerLocationIds,
      queryTags,
      schemaName,
    ])

    const apiFilterOptions = transformApiOptionsIntoDashboardOptions(
      data?.insightsPortalServiceGetInsightsPortalFilterOptions?.filterOptions,
      transformApiOptionsConfig
    )

    const dashboardFilterOptions = Object.entries(apiFilterOptions).reduce(
      (dashboardFilterOptions, [apiFilterName, filterOptions]) => {
        dashboardFilterOptions[mapApiNameToDashboardName[apiFilterName]] = filterOptions

        return dashboardFilterOptions
      },
      {} as BaseDynamicFilterOptions
    )

    return {
      filterOptions: dashboardFilterOptions,
      loading,
      error,
      called,
    }
  }
  return {
    useFilters,
  }
}

/*
  this is a factory that creates hooks that allow you to interact with the useGetInsightsPortalMetricsQuery query for a specific schema.
  user needs to define which schema to use, the metric names, and a transformation function that transforms the filter selections for the api.
  check spec file for usage.
*/

export function createSchemaMetricsHooks<TMetricEnum extends string>(
  schema: InsightsPortalSchema,
  transformFilters: ReturnType<typeof transformFilterParamsToApiFilterSelectionsFactory>
) {
  const useMetrics = (
    selectedFiltersValues: FilterParamsState['selectedFiltersValues'],
    dates: { from: Moment; to: Moment },
    metrics: Array<TMetricEnum>,
    skip = false
  ) => {
    const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
    const from = dates.from.clone().startOf('day')
    const to = dates.to.clone().endOf('day')
    const currentPeriodDateRange = {
      from,
      to,
    }
    const queryTags = useInsightsPortalQueryTags()
    const { data, loading, called } = useGetInsightsPortalMetricsQuery({
      variables: {
        schema: useSchema(schema),
        partnerIds,
        retailerIds,
        retailerLocationIds,
        metrics,
        filters: transformFilters(selectedFiltersValues, currentPeriodDateRange),
        owner: InsightsPortalOwner.RetailerTools,
        queryTags,
      },
      skip,
    })
    const apiMetrics = data?.insightsPortalServiceGetInsightsPortalMetricValues?.metricValues || []

    const metricsObject = metrics.reduce((metricsObject, metric) => {
      const metricValueObject = apiMetrics.find(apiMetric => apiMetric.metric === metric)

      if (!metricValueObject) {
        return {
          ...metricsObject,
          [metric]: null,
        }
      }

      const valueTypes: Array<keyof InsightsPortalValueInput> = [
        'doubleValue',
        'intValue',
        'stringValue',
        'timestampValue',
      ]

      const metricValue = Object.entries(metricValueObject.value || {}).find(
        ([key, value]) =>
          // makes sure the key is in valueTypes because the graphql __typename is a key that will be truthy
          // we are ensuring that we are only checking real values
          valueTypes.includes(key as keyof InsightsPortalValueInput) && Boolean(value)
      )?.[1]

      return {
        ...metricsObject,
        [metric]: String(metricValue),
      }
    }, {}) as { [key in TMetricEnum]: string }

    return {
      data: metricsObject,
      loading,
      called,
    }
  }

  const useCurrentPeriodMetrics = (
    metrics: TMetricEnum[],
    skip = false,
    dateObjOverride?: Omit<FilterParamsState['dates'], 'dateRange'>,
    filtersObjOverride?: { [key: string]: SimpleOption[] | string }
  ) => {
    const { selectedFiltersValues, dates: currentFilterDates } = useFilterParamsContext()
    const dates = dateObjOverride || currentFilterDates
    const from = dates.from.clone().startOf('day')
    const to = dates.to.clone().endOf('day')
    const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }
    const currentPeriodDateRange = {
      from,
      to,
    }

    return useMetrics(filterValues, currentPeriodDateRange, metrics, skip)
  }

  const useLastPeriodMetrics = (
    metrics: TMetricEnum[],
    skip = false,
    comparisonTypeOverride?: 'lastPeriod' | 'lastYear',
    dateObjOverride?: Omit<FilterParamsState['dates'], 'dateRange'>,
    filtersObjOverride?: { [key: string]: SimpleOption[] | string }
  ) => {
    const { selectedFiltersValues, dates: currentFilterDates } = useFilterParamsContext()
    const dates = dateObjOverride || currentFilterDates
    const from = dates.from.clone().startOf('day')
    const to = dates.to.clone().endOf('day')
    const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }
    const lastPeriodDateRange = calculateComparisonPeriod(
      from,
      to,
      comparisonTypeOverride || selectedFiltersValues.comparisonType
    )

    return useMetrics(filterValues, lastPeriodDateRange, metrics, skip)
  }

  return {
    useMetrics,
    useCurrentPeriodMetrics,
    useLastPeriodMetrics,
  }
}

/*
  this is a factory that creates hooks that allow you to interact with the useGetInsightsPortalGroupComparisonQuery query for a specific schema.
  user needs to define which schema to use, the metric, and a transformation function that transforms the filter selections for the api.
  check spec file for usage.
*/

type UseCompareGroupedPeriods = {
  metric: string
  groupBy: string
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  dateObjOverride?: Omit<FilterParamsState['dates'], 'dateRange'>
  skip?: boolean
  primarySecondaryMetrics?: string[]
} & Pick<
  GetInsightsPortalGroupComparisonQueryVariables,
  'orderBy' | 'orderDirection' | 'limit' | 'offset' | 'joinBy'
>

export function createCompareGroupedPeriodsHooks(
  schema: InsightsPortalSchema,
  transformFilters: ReturnType<typeof transformFilterParamsToApiFilterSelectionsFactory>
) {
  const useComparison = ({
    metric,
    groupBy,
    orderBy,
    orderDirection,
    limit,
    offset,
    joinBy,
    filtersObjOverride = null,
    dateObjOverride,
    skip = false,
    primarySecondaryMetrics,
  }: UseCompareGroupedPeriods) => {
    const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
    const { selectedFiltersValues, dates: currentFilterDates } = useFilterParamsContext()
    const dates = dateObjOverride || currentFilterDates
    const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }
    const from = dates.from.clone().startOf('day')
    const to = dates.to.clone().endOf('day')
    const currentPeriodDateRange = {
      from,
      to,
    }
    const lastPeriodDateRange = calculateComparisonPeriod(from, to, filterValues.comparisonType)
    const currentPeriodFilters = transformFilters(filterValues, currentPeriodDateRange)
    const lastPeriodFilters = transformFilters(filterValues, lastPeriodDateRange)
    const primaryDatasetInput = {
      schema: useSchema(schema),
      filters: currentPeriodFilters,
      metric,
      groupBy,
      secondaryMetrics: primarySecondaryMetrics,
    }
    const comparisonDatasetInput = {
      schema: useSchema(schema),
      filters: lastPeriodFilters,
      metric,
      groupBy,
    }
    const queryTags = useInsightsPortalQueryTags()
    const variables: GetInsightsPortalGroupComparisonQueryVariables = {
      partnerIds,
      retailerIds,
      retailerLocationIds,
      primaryDataset: primaryDatasetInput,
      comparisonDataset: comparisonDatasetInput,
      joinBy: joinBy || InsightsPortalGroupComparisonRequestJoinBy.RowNumber,
      orderBy,
      orderDirection,
      limit,
      offset,
      owner: InsightsPortalOwner.RetailerTools,
      queryTags,
    }
    const { data, ...rest } = useGetInsightsPortalGroupComparisonQuery({
      variables,
      skip: skip,
    })

    return {
      data: data?.insightsPortalServiceGetInsightsPortalGroupComparison?.values || [],
      ...rest,
    }
  }
  return {
    useComparison,
  }
}

type UseMultiGroupQuery = {
  metrics: string[]
  groupBys: string[]
  skip?: boolean
  includeHistoricalDate?: boolean
  historicalGroupBy?: string
  filtersObjOverride?: { [key: string]: SimpleOption[] | string }
  dateObjOverride?: Omit<FilterParamsState['dates'], 'dateRange'>
} & Pick<GetInsightsPortalMultiGroupDataQueryVariables, 'orderBys' | 'limit' | 'offset'>

export function createMultiGroupDataHook(
  schema: InsightsPortalSchema,
  transformFilters: ReturnType<typeof transformFilterParamsToApiFilterSelectionsFactory>
) {
  const useMultiGroupData = ({
    metrics,
    groupBys,
    limit,
    offset,
    skip,
    orderBys,
    includeHistoricalDate,
    historicalGroupBy,
    filtersObjOverride = null,
    dateObjOverride,
  }: UseMultiGroupQuery) => {
    const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
    const { selectedFiltersValues, dates: currentFilterDates } = useFilterParamsContext()
    const dates = dateObjOverride || currentFilterDates
    const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

    const from = dates.from.clone().startOf('day')
    const to = dates.to.clone().endOf('day')
    const currentPeriodDateRange = {
      from,
      to,
    }

    const queryTags = useInsightsPortalQueryTags()

    const lastPeriodDateRange = calculateComparisonPeriod(from, to, filterValues.comparisonType)

    const apiFilters = transformFilters(filterValues, currentPeriodDateRange)

    if (includeHistoricalDate) {
      apiFilters.push({
        name: 'DATE_PT_HISTORICAL',
        betweenFilter: {
          start: {
            timestampValue: lastPeriodDateRange.from.utc(true).format(insightsPortalDateFormat),
          },
          end: {
            timestampValue: lastPeriodDateRange.to.utc(true).format(insightsPortalDateFormat),
          },
        },
      })
      groupBys.push(historicalGroupBy || 'DATE_PT_HISTORICAL')
    }

    const { data, loading, called, error } = useGetInsightsPortalMultiGroupDataQuery({
      variables: {
        schema: useSchema(schema),
        partnerIds,
        retailerIds,
        retailerLocationIds,
        metrics,
        groupBys,
        limit,
        offset,
        orderBys: orderBys || [],
        filters: apiFilters,
        owner: InsightsPortalOwner.RetailerTools,
        queryTags,
      },
      skip,
    })
    return {
      data,
      loading,
      called,
      error,
    }
  }

  return {
    useMultiGroupData,
  }
}
